import type { AdsCreativeRequest, ContentKind, FormatPreset } from './api.ts';

/**
 * How an ads creative request (meta-ads-agent US-019) fills the New Content
 * form: the kind from its format, a name from its concept, the format presets
 * cut to its aspect ratios, and a brief that carries everything the agent said.
 */

/** Mirrors the New Content page's name rule: letters, numbers, `-` and `_`, at most 60. */
export function contentNameFromConcept(concept: string): string {
  return concept
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
}

export function contentKindOfRequest(request: Pick<AdsCreativeRequest, 'format'>): ContentKind | null {
  return request.format === 'video' || request.format === 'image' ? request.format : null;
}

/** The first preset of each requested ratio the kind can be rendered as, in the request's order. */
export function presetsForRatios(presets: readonly FormatPreset[], ratios: readonly string[], kind: ContentKind): string[] {
  const chosen: string[] = [];
  for (const ratio of ratios) {
    const match = presets.find((preset) => preset.aspect === ratio && preset.kinds.includes(kind));
    if (match !== undefined && !chosen.includes(match.preset)) chosen.push(match.preset);
  }
  return chosen;
}

export function briefFromRequest(request: AdsCreativeRequest): string {
  const lines = [`A Meta ad for the ads agent's creative request: ${request.concept}.`];
  if (request.angle !== null) lines.push(`Angle: ${request.angle}`);
  if (request.hook !== null) {
    lines.push(request.format === 'video' ? `Hook (the first 3 seconds): ${request.hook}` : `Hook: ${request.hook}`);
  }
  if (request.aspectRatios.length > 0) lines.push(`Aspect ratios: ${request.aspectRatios.join(', ')}`);
  if (request.notes !== null) lines.push(`Why the agent wants it: ${request.notes}`);
  return lines.join('\n\n');
}
